import * as Yup from 'yup';
import {FormNameEnum, UploadFormLabelEnum} from '../../enums';
import {errorMessages} from 'utils/constants';
import {IInputFile} from 'models/library/common';
import {getIsDateEarlierFromToday, isValidFormValue} from '../helpers';
import {ALLOWED_MIMETYPE_FORMATS} from 'admin/resources';
import {
  DIRECTORY_LINK_FORMAT_MESSAGE,
  isRegisteredResourceFieldRequired,
  isValidDirectoryLink,
} from 'admin/resources/utils';
import {FormNameEnum as SharedFormNameEnum} from 'enums/form';

export interface IUploadFileFormBaseValues {
  [FormNameEnum.file]: IInputFile | any;
  [FormNameEnum.isAccessibleToStudents]: boolean;
  [FormNameEnum.hasUploadPermissions]: boolean;
  [FormNameEnum.displayName]: string;
  [FormNameEnum.keyTerms]: string;
  [FormNameEnum.programs]: number[];
  [FormNameEnum.pillars]: number[];
  [FormNameEnum.competencies]: number[];
  [FormNameEnum.gradeLevels]: number[];
  [FormNameEnum.skillsAndBehaviors]: number[];
  [FormNameEnum.languages]: number[];
  [FormNameEnum.knowledgeSkillsAndAbilities]: number[];
  [FormNameEnum.briefDescription]: string;
  [FormNameEnum.learningObjectives]: string;
  [FormNameEnum.expirationDate]: string | Date;
  [FormNameEnum.shouldDisableFileType]: boolean;
  [FormNameEnum.fileType]: number;

  [FormNameEnum.id]?: string;
  [FormNameEnum.fileName]?: string;
  [FormNameEnum.link]?: string;

  [FormNameEnum.stockImageLinkNumber]?: string;
  [FormNameEnum.permissionNeeded]?: boolean | string | null;
  [FormNameEnum.imageSources]?: number[];
  [FormNameEnum.ethnicities]?: number[];
  [FormNameEnum.genders]?: number[];
  [FormNameEnum.publishedOutputTool]?: number;
  [FormNameEnum.alternateText]?: string;
  [FormNameEnum.linkToStockImageSource]?: string;

  [FormNameEnum.slideNumber]?: number;
  [FormNameEnum.sessionThemeNumber]?: number;

  [FormNameEnum.associatedResources]?: Array<string | {id: string; name?: string}>;
  [FormNameEnum.numberOfResources]?: number;
  [FormNameEnum.resourceIds]?: string[];

  [FormNameEnum.characterTraits]?: number[];
  [FormNameEnum.behavioralIndicators]?: number[];
  [FormNameEnum.videoTranscriptionLink]?: string;
  [FormNameEnum.credentialing]?: boolean | string | null;
  [FormNameEnum.permissionInPerpetuity]?: boolean;
  [FormNameEnum.accessibilityStatus]?: number | null;
  [FormNameEnum.accessibilityReports]?: string;

  [FormNameEnum.urlSource]?: string;
  [FormNameEnum.usageType]?: number;
  [FormNameEnum.reuseFlag]?: boolean | string | null;
  [FormNameEnum.notes]?: string;
  [FormNameEnum.permissionAgreementUrl]?: string;
}

/**
 * Upload = new record → always enforce all required fields (createdUtc omitted).
 * Pass createdUtc only if you need the same conditional rules as edit.
 */
export const buildUploadFileFormBaseValidationSchema = (createdUtc?: string | null) => {
  const requireFile = isRegisteredResourceFieldRequired(SharedFormNameEnum.file, createdUtc);
  const requireFileType = isRegisteredResourceFieldRequired(SharedFormNameEnum.fileType, createdUtc);
  const requireDisplayName = isRegisteredResourceFieldRequired(SharedFormNameEnum.displayName, createdUtc);
  const requireLanguages = isRegisteredResourceFieldRequired(SharedFormNameEnum.languages, createdUtc);
  const requireStudentFacing = isRegisteredResourceFieldRequired(SharedFormNameEnum.isAccessibleToStudents, createdUtc);
  const requireUploadPermissions = isRegisteredResourceFieldRequired(
    SharedFormNameEnum.hasUploadPermissions,
    createdUtc
  );
  const requireDirectoryLink = isRegisteredResourceFieldRequired(SharedFormNameEnum.link, createdUtc);
  const requirePrograms = isRegisteredResourceFieldRequired(SharedFormNameEnum.programs, createdUtc);
  const requireBriefDescription = isRegisteredResourceFieldRequired(SharedFormNameEnum.briefDescription, createdUtc);
  const requireKeyTerms = isRegisteredResourceFieldRequired(SharedFormNameEnum.keyTerms, createdUtc);

  let fileSchema: Yup.MixedSchema = Yup.mixed().nullable();
  if (requireFile) {
    fileSchema = fileSchema.required(`You should attach a ${UploadFormLabelEnum.File}!`);
  }
  fileSchema = fileSchema.test('is-filetype-allowed', 'This file format is not allowed!', (value: IInputFile) =>
    value ? ALLOWED_MIMETYPE_FORMATS.includes(value.type) : true
  );

  let fileTypeSchema = Yup.number().nullable();
  if (requireFileType) {
    fileTypeSchema = fileTypeSchema.test(
      'is-prepulated',
      errorMessages.requiredWithInputName(`an ${UploadFormLabelEnum.FileType}`),
      function () {
        return isValidFormValue(this.parent.file);
      }
    );
  }

  let languagesSchema: Yup.ArraySchema<number> = Yup.array().of(Yup.number());
  if (requireLanguages) {
    languagesSchema = languagesSchema.required('You should select at least one language!');
  }

  let studentFacingSchema = Yup.boolean().nullable();
  if (requireStudentFacing) {
    studentFacingSchema = studentFacingSchema.required('You should choose if this file is Student Facing!');
  }

  let uploadPermissionsSchema = Yup.boolean();
  if (requireUploadPermissions) {
    uploadPermissionsSchema = uploadPermissionsSchema.test(
      'is-checked',
      'You should accept upload file permissions!',
      function () {
        return isValidFormValue(this.parent.hasUploadPermissions);
      }
    );
  }

  let displayNameSchema = Yup.string().nullable();
  if (requireDisplayName) {
    displayNameSchema = displayNameSchema.required(
      errorMessages.requiredWithInputName(`a ${UploadFormLabelEnum.DisplayName}`)
    );
  }

  let briefDescriptionSchema = Yup.string()
    .nullable()
    .max(500, 'Brief description should be less than 500 characters!');
  if (requireBriefDescription) {
    briefDescriptionSchema = briefDescriptionSchema.required(
      errorMessages.requiredWithInputName(`a ${UploadFormLabelEnum.BriefDescription}`)
    );
  }

  let programsSchema: Yup.ArraySchema<number> = Yup.array().of(Yup.number());
  if (requirePrograms) {
    programsSchema = programsSchema.min(1, errorMessages.requiredWithFieldName(`a ${UploadFormLabelEnum.Program}`));
  }

  let keyTermsSchema = Yup.string().nullable().max(250, 'Keywords should be less than 250 characters!');
  if (requireKeyTerms) {
    keyTermsSchema = keyTermsSchema.required(errorMessages.requiredWithInputName(UploadFormLabelEnum.KeyTerms));
  }

  let directoryLinkSchema = Yup.string()
    .nullable()
    .test('is-directory-link-format', DIRECTORY_LINK_FORMAT_MESSAGE, (value) => isValidDirectoryLink(value));
  if (requireDirectoryLink) {
    directoryLinkSchema = directoryLinkSchema.required(errorMessages.requiredWithInputName('a Directory Link'));
  }

  return Yup.object().shape({
    [FormNameEnum.file]: fileSchema,
    [FormNameEnum.fileType]: fileTypeSchema,
    [FormNameEnum.briefDescription]: briefDescriptionSchema,
    [FormNameEnum.gradeLevels]: Yup.array().of(Yup.number()),
    [FormNameEnum.programs]: programsSchema,
    [FormNameEnum.pillars]: Yup.array().of(Yup.number()),
    [FormNameEnum.competencies]: Yup.array().of(Yup.number()),
    [FormNameEnum.knowledgeSkillsAndAbilities]: Yup.array().of(Yup.number()),
    [FormNameEnum.languages]: languagesSchema,
    [FormNameEnum.expirationDate]: Yup.date()
      .nullable()
      .typeError('Expected date format: (mm/dd/yyyy)')
      .test('is-date-earlier-than-now', 'The selected date should be in the future.', function () {
        const {expirationDate} = this.parent;

        // If the datepicker is cleaned up, then validation is true
        return getIsDateEarlierFromToday(expirationDate);
      }),
    [FormNameEnum.keyTerms]: keyTermsSchema,
    [FormNameEnum.learningObjectives]: Yup.string()
      .nullable()
      .max(250, 'Learning objectives should be less than 250 characters!'),
    [FormNameEnum.isAccessibleToStudents]: studentFacingSchema,
    [FormNameEnum.hasUploadPermissions]: uploadPermissionsSchema,
    [FormNameEnum.displayName]: displayNameSchema,

    [FormNameEnum.stockImageLinkNumber]: Yup.string().nullable(),
    [FormNameEnum.fileName]: Yup.string().nullable(),
    [FormNameEnum.link]: directoryLinkSchema,
    [FormNameEnum.id]: Yup.string().nullable(),
    [FormNameEnum.permissionNeeded]: Yup.mixed().nullable(),
    [FormNameEnum.imageSources]: Yup.array().of(Yup.number()),
    [FormNameEnum.ethnicities]: Yup.array().of(Yup.number()),
    [FormNameEnum.genders]: Yup.array().of(Yup.number()),
    [FormNameEnum.publishedOutputTool]: Yup.number().nullable(),
    [FormNameEnum.alternateText]: Yup.string().nullable(),
    [FormNameEnum.linkToStockImageSource]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Link to Stock Image Source must be a valid url!'),

    [FormNameEnum.slideNumber]: Yup.number().min(0).max(999).nullable(),
    [FormNameEnum.sessionThemeNumber]: Yup.number().nullable(),

    [FormNameEnum.associatedResources]: Yup.array(),
    [FormNameEnum.numberOfResources]: Yup.number().nullable().min(0),
    [FormNameEnum.resourceIds]: Yup.array().of(Yup.string()),

    [FormNameEnum.characterTraits]: Yup.array().of(Yup.number()),
    [FormNameEnum.behavioralIndicators]: Yup.array().of(Yup.number()),
    [FormNameEnum.videoTranscriptionLink]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Video Transcript Link must be a valid url!'),
    [FormNameEnum.credentialing]: Yup.mixed().nullable(),
    [FormNameEnum.permissionInPerpetuity]: Yup.boolean(),
    [FormNameEnum.accessibilityStatus]: Yup.mixed().nullable(),
    [FormNameEnum.accessibilityReports]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Accessibility Reports must be a valid url!'),

    [FormNameEnum.urlSource]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Media Rights URL Source must be a valid url!'),
    [FormNameEnum.usageType]: Yup.number().nullable(),
    [FormNameEnum.reuseFlag]: Yup.mixed().nullable(),
    [FormNameEnum.notes]: Yup.string().nullable(),
    [FormNameEnum.permissionAgreementUrl]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Permission Agreement URL must be a valid url!'),
  });
};

/** New uploads always enforce all required fields. */
export const uploadFileFormBaseValidationSchema = buildUploadFileFormBaseValidationSchema();
